"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  id: "01",
  title: "Consultation & Design",
  description: "We begin with a thorough site visit to discuss your vision, assess the technical requirements of your space, and provide a detailed, transparent quote. We help select the perfect materials for a luxury finish."
}, {
  id: "02",
  title: "Strip-out & Preparation",
  description: "Our team carefully removes old fixtures and tiles, ensuring a clean slate. We focus heavily on substrate preparation, including self-levelling floors and structural reinforcements, to guarantee longevity."
}, {
  id: "03",
  title: "Technical Installation",
  description: "The core of our work involves installing waterproof tanking systems, Ditra matting, and precision tiling. We specialise in 45-degree mitred edges and epoxy grout for a seamless, high-end look."
}, {
  id: "04",
  title: "Final Polish & Handover",
  description: "We install your chosen bathroom fixtures, apply premium silicone sealing, and conduct a deep clean. The project isn't complete until we've achieved a showroom-quality finish and you are 100% satisfied."
}];
export default function OurStepByStepProcess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="our-step-by-step-process" ref={ref} className={cn("bg-background relative overflow-hidden py-24 sm:py-32 transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
      <div className="container px-8">
        <div className="max-w-3xl text-left mb-20">
          <h2 className="font-serif text-4xl font-bold tracking-tight text-primary sm:text-5xl mb-6">
            Our Step-by-Step Process
          </h2>
          <p className="text-lg leading-8 text-primary/80">
            From the initial consultation to the final polish, we follow a rigorous 4-stage workflow to ensure precision, durability, and a luxury finish for every bathroom renovation.
          </p>
        </div>

        <div className="relative max-w-4xl">
          {}
          <div className="absolute left-4 top-0 bottom-0 w-px bg-border md:left-8" />

          <div className="space-y-16">
            {steps.map((step, i) => <div key={step.id} data-index={i} className="relative pl-12 md:pl-24 group transition-all duration-500" style={{
            transitionDelay: `${i * 150}ms`
          }}>
                {}
                <div className="absolute left-0 top-0 flex h-8 w-8 items-center justify-center rounded-sm bg-primary text-white z-10 md:h-16 md:w-16">
                  <span className="text-sm font-bold md:text-xl font-serif">{step.id}</span>
                </div>

                {}
                <div className="absolute left-16 -top-8 md:-top-12 md:left-24 select-none pointer-events-none opacity-5">
                  <span className="font-serif text-7xl md:text-9xl font-bold tracking-tighter" style={{
                WebkitTextStroke: "1px var(--color-primary)",
                color: "transparent"
              }}>
                    {step.id}
                  </span>
                </div>

                {}
                <div className="border border-border p-8 rounded-sm bg-accent/30 hover:border-primary/30 transition-all duration-300">
                  <h3 className="font-serif text-2xl font-semibold mb-3 text-primary">
                    {step.title}
                  </h3>
                  <p className="leading-relaxed text-primary/70">
                    {step.description}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        <div className="mt-20 flex justify-start">
          <Button asChild size="lg" className="h-12 px-8 text-base bg-[#a68d59] hover:bg-[#947e4f] text-white border-none rounded-sm shadow-none transition-all">
            <Link to="#">
              Start Your Project <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}